// Server events (shared/events.ts) on the client: a chip on the hunt card
// while one runs, and the schedule on Help. The list comes with hello and as
// { t: 'events', list } whenever an admin changes it.
import { EVENTS, EVENT_KINDS, eventActive, eventLabel, eventMult, type EventKind, type ServerEvent } from '../shared/events.ts';
import { REGIONS } from '../shared/grounds.ts';
import { esc } from './fmt.ts';

let list: ServerEvent[] = [];

export function setEvents(l: unknown) { list = Array.isArray(l) ? (l as ServerEvent[]) : []; }
export function serverEvents(): readonly ServerEvent[] { return list; }

/** "1h 20m", "2d 4h", "12m" */
export function eventLeft(ms: number): string {
  const m = Math.max(1, Math.ceil(ms / 60_000));
  if (m >= 2 * 1440) return `${Math.floor(m / 1440)}d ${Math.floor((m % 1440) / 60)}h`;
  if (m >= 60) return `${Math.floor(m / 60)}h ${m % 60}m`;
  return `${m}m`;
}

/** "Sat 14 Nov 18:00 UTC" */
export function utcWhen(ts: number): string {
  const d = new Date(ts);
  return `${d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })} ${d.toISOString().slice(11, 16)} UTC`;
}

/**
 * Running events by kind, with the multiplier they give together (capped, as the server pays it); events for
 * another region's hunts group by kind and region, with `here` false.
 */
export function eventGroups(now: number, region: string): { key: string; kind: EventKind; mult: number; region?: string; end: number; here: boolean }[] {
  const groups = new Map<string, ServerEvent[]>();
  for (const e of list) {
    if (!eventActive(e, now)) continue;
    const key = !e.region || e.region === region ? e.kind : `${e.kind}-${e.region}`;
    groups.set(key, [...(groups.get(key) ?? []), e]);
  }
  return [...groups].map(([key, g]) => {
    const here = !g[0].region || g[0].region === region;
    return {
      key, kind: g[0].kind, here, end: Math.max(...g.map((e) => e.end)),
      mult: eventMult(g, g[0].kind, here ? region : g[0].region!, now),
      region: g.every((e) => e.region) ? g[0].region : undefined,
    };
  });
}

/** One chip per kind of running event; one for another region's hunts is dimmed. */
export function eventChips(now: number, region: string): string {
  return eventGroups(now, region).map((g) => {
    const left = eventLeft(g.end - now);
    const tip = `×${+g.mult.toFixed(2)} ${EVENT_KINDS[g.kind].what}${g.region ? ` on ${REGIONS.find((x) => x.id === g.region)?.name ?? g.region} hunts` : ''} · ${left} left`;
    return `<a class="bchip ev-chip${g.here ? '' : ' idle'}" data-key="ev-${g.key}" href="#/help/events" title="${esc(tip)}"><b>${esc(eventLabel({ id: 0, start: 0, ...g }))}</b><em>${left}</em></a>`;
  }).join('');
}

/** Help section: the rules and the schedule. */
export function eventsHelp(now: number): string {
  const rows = list.filter((e) => e.end > now).map((e) => `<li class="${eventActive(e, now) ? 'on' : ''}"><b>${esc(eventLabel(e))}</b><span>${eventActive(e, now) ? `now, ${eventLeft(e.end - now)} left` : `${esc(utcWhen(e.start))} · ${eventLeft(e.end - e.start)}`}</span></li>`).join('');
  return `<h4 id="events">Events</h4><p>Now and then everyone gets a free event: ×${EVENTS.minMult} to ×${EVENTS.maxMult} kill XP, drop chance or kill coins, sometimes on one region's hunts only. It multiplies with every other boost, and two of the same kind multiply up to ×${EVENTS.maxMult}. Events never touch Aura odds or diamonds, and are never sold. A running one shows on the hunt card; the World channel says when one starts.</p>
    ${rows ? `<ul class="ev-list">${rows}</ul>` : '<p class="muted">None scheduled.</p>'}`;
}
